export type WeatherData = {
  conditions: string;
  // Holds Fahrenheit value. Name retained to avoid rename cascade; see KI-036.
  temperatureCelsius: number;
  windSpeedKnots: number;
  windDirection: string;
  visibilityNm: number;
  precipitation: string;
  fetchedAt: string;
  latitude: number;
  longitude: number;
};

const OPEN_METEO_BASE = 'https://api.open-meteo.com/v1/forecast';

function weatherCodeToCondition(code: number): string {
  if (code === 0) return 'Clear Sky';
  if (code <= 3) return 'Partly Cloudy';
  if (code === 45 || code === 48) return 'Foggy';
  if (code >= 51 && code <= 55) return 'Drizzle';
  if (code >= 61 && code <= 65) return 'Rain';
  if (code >= 71 && code <= 75) return 'Snow';
  if (code >= 80 && code <= 82) return 'Rain Showers';
  if (code === 95) return 'Thunderstorm';
  if (code >= 96 && code <= 99) return 'Thunderstorm with Hail';
  return 'Unknown';
}

function degreesToCardinal(degrees: number): string {
  const directions = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
  ];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

function classifyPrecipitation(mm: number): string {
  if (mm === 0) return 'None';
  if (mm < 2.5) return 'Light';
  if (mm < 7.6) return 'Moderate';
  return 'Heavy';
}

export async function fetchWeatherForLocation(
  lat: number,
  lng: number,
): Promise<WeatherData | null> {
  try {
    const url =
      `${OPEN_METEO_BASE}?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,wind_speed_10m,wind_direction_10m,precipitation,weather_code,visibility` +
      `&temperature_unit=fahrenheit&wind_speed_unit=kn`;

    const response = await fetch(url);
    if (!response.ok) return null;

    const json = await response.json();
    const current = json?.current;
    if (!current) return null;

    return {
      conditions: weatherCodeToCondition(current.weather_code ?? -1),
      temperatureCelsius: current.temperature_2m ?? 0,
      windSpeedKnots: current.wind_speed_10m ?? 0,
      windDirection: degreesToCardinal(current.wind_direction_10m ?? 0),
      visibilityNm: Math.round(((current.visibility ?? 0) / 1852) * 10) / 10,
      precipitation: classifyPrecipitation(current.precipitation ?? 0),
      fetchedAt: new Date().toISOString(),
      latitude: lat,
      longitude: lng,
    };
  } catch {
    return null;
  }
}

function getBrowserPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

export async function getCurrentLocationWeather(): Promise<WeatherData | null> {
  const pos = await getBrowserPosition();
  if (!pos) return null;
  return fetchWeatherForLocation(pos.coords.latitude, pos.coords.longitude);
}
